import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import {
  GenerateQuestionsDTO,
  PublishQuestionsDTO,
  QuestionDTO,
  QuestionQueryDTO,
} from "@onetutor/shared/dto";
import {
  ModelTypes,
  QuestionDifficulty,
  QuestionGenerationModels,
  SupportedLanguage,
} from "@onetutor/shared/type";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";
import Header from "../../components/header/Header";
import Modal, { ModalVariant } from "../../components/modal";
import { useNavigationBlocker, useToastEffect } from "../../hooks";
import { useGetCourseQuery } from "../../services/courseApi";
import {
  useDeleteQuestionMutation,
  useGenerateQuestionsMutation,
  useGetQuestionsQuery,
  useGetTopicsQuery,
  usePublishQuestionsMutation,
} from "../../services/quizApi";
import { showToast } from "../../slices/statusSlice";
import { EditQuestionCard } from "./EditQuestionCard";
import { EditQuestionModal } from "./EditQuestionModal";
import { QuestionsSkeletonContainer } from "./QuestionsSkeletonContainer";

type TopicsType = {
  name: string;
  isActive: boolean;
  showInSearch: boolean;
};

function ManageQuizPage() {
  const [pageTypeParams, setPageTypeParams] = useSearchParams();
  const isEditing = pageTypeParams.get("edit") === "true";

  const { courseId } = useParams();
  if (!courseId) {
    throw new Error("CourseId is not defined");
  }
  const { data: course } = useGetCourseQuery(courseId);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation(["manageQuizPage", "header", "commons"]);

  const DIFFICULTY_ALL = "all";

  const activeDifficulty = pageTypeParams.get("difficulty");
  const activeTopic = pageTypeParams.get("topic");
  const activeShouldFetchAll =
    pageTypeParams.get("fetchAll") === "true" || false;

  const [shouldFetch, setShouldFetch] = useState(true);
  const [isShouldFetchAll, setIsShouldFetchAll] = useState(false);
  const [topics, setTopics] = useState<TopicsType[]>([]);
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState<QuestionDifficulty>(
    QuestionDifficulty.EASY,
  );
  const [language, setLanguage] = useState<SupportedLanguage>(
    SupportedLanguage.EN,
  );
  const [modelType, setModelType] = useState<ModelTypes>(ModelTypes.GPT_4O);
  const availableModels = QuestionGenerationModels.getAll();
  const [searchInput, setSearchInput] = useState<string>("");
  const [questionConfig, setQuestionConfig] = useState<QuestionQueryDTO>({});
  const [questionToEdit, setQuestionToEdit] = useState<QuestionDTO>();
  const [resetEditMask, setResetEditMask] = useState<boolean>(false);
  const [questions, setQuestions] = useState<QuestionDTO[]>([]);

  const [deleteQuestion, { isError: isErrorDelete, error: errorDelete }] =
    useDeleteQuestionMutation();

  const { data: topicsData } = useGetTopicsQuery({
    courseId,
    queryArgs: { includeUnpublished: true },
  });

  const {
    data: fetchedQuestions,
    isError: isErrorGetQuestions,
    error: errorGetQuestions,
  } = useGetQuestionsQuery(
    {
      courseId: courseId,
      queryParams: questionConfig,
    },
    { skip: !activeTopic || !shouldFetch },
  );

  useToastEffect({
    isError: isErrorGetQuestions,
    error: errorGetQuestions,
  });

  const handleDelete = async (questionId: string) => {
    await deleteQuestion({ courseId, questionId });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isEditing) {
      if (
        activeTopic === topic &&
        activeDifficulty === difficulty &&
        activeShouldFetchAll === isShouldFetchAll
      ) {
        return;
      }
      setQuestions([]);
      setShouldFetch(true);
      setPageTypeParams({
        edit: "true",
        topic: topic,
        difficulty: difficulty,
        fetchAll: isShouldFetchAll ? "true" : "false",
      });
    } else {
      const generateQuestionsConfig: GenerateQuestionsDTO = {
        difficulty,
        topic,
        language,
        keywords,
        userInstructions: userInstructions || null,
        modelType,
        countFreeTextQuestions,
        countMultipleChoiceQuestions,
      };

      const response = await generateQuestions({
        courseId,
        generateQuestionsConfig,
      });

      if (response.data) {
        setQuestions(response.data.questions);
      }
    }
  };

  useEffect(() => {
    if (topicsData?.topics) {
      setTopics(
        topicsData.topics.map((topic) => {
          return {
            name: topic.name,
            isActive: false,
            showInSearch: true,
          };
        }),
      );
    }
  }, [topicsData]);

  const fetchQuestions = () => {
    const questionConfig: QuestionQueryDTO = {
      topic: activeTopic || "",
      ...(!isShouldFetchAll && {
        difficulty: activeDifficulty as QuestionDifficulty,
      }),
    };
    setQuestionConfig(questionConfig);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO fix dependencies
  useEffect(() => {
    if (activeTopic || activeDifficulty) {
      setShouldFetch(true);
      fetchQuestions();
    }
  }, [activeTopic, activeDifficulty]);

  useEffect(() => {
    if (fetchedQuestions) {
      if (fetchedQuestions.questions.length === 0) {
        dispatch(
          showToast({
            message: t("infoNoQuestions"),
            type: "info",
          }),
        );
      }

      setQuestions(fetchedQuestions.questions);
    }
  }, [fetchedQuestions, dispatch, t]);

  //Only for Generate Page
  const MAX_QUESTIONS_MC = 10;
  const DEFAULT_QUESTIONS_MC = 8;
  const MAX_QUESTIONS_FREE_TEXT = 10;
  const DEFAULT_QUESTIONS_FREE_TEXT = 8;

  const [keywords, setKeywords] = useState("");
  const [userInstructions, setUserInstructions] = useState("");
  const [countMultipleChoiceQuestions, setCountMultipleChoiceQuestions] =
    useState(DEFAULT_QUESTIONS_MC);
  const [countFreeTextQuestions, setCountFreeTextQuestions] = useState(
    DEFAULT_QUESTIONS_FREE_TEXT,
  );

  const handlePublish = async () => {
    const questionsToPublish: PublishQuestionsDTO = {
      questionIds: questions.map((question) => question.id),
    };
    await publishQuestions({ courseId, body: questionsToPublish });
    setQuestions([]);
  };

  const [
    generateQuestions,
    {
      isLoading: isLoadingGenerate,
      isError: isErrorGenerate,
      error: errorGenerate,
      isSuccess: isSuccessGenerate,
    },
  ] = useGenerateQuestionsMutation();

  useEffect(() => {
    if (isSuccessGenerate) {
      window.scrollBy({
        top: window.innerHeight,
        behavior: "smooth",
      });
    }
  }, [isSuccessGenerate]);

  const [
    publishQuestions,
    {
      isError: isErrorPublish,
      isSuccess: isSuccessPublish,
      error: errorPublish,
    },
  ] = usePublishQuestionsMutation();
  useToastEffect({
    isError: isErrorGenerate,
    error: errorGenerate,
  });
  useToastEffect({
    isError: isErrorDelete,
    error: errorDelete,
  });
  useToastEffect({
    isError: isErrorPublish,
    isSuccess: isSuccessPublish,
    error: errorPublish,
  });

  const navBlockerWhen =
    (!isEditing && questions.length > 0) || isLoadingGenerate;
  const navBlockerMessage = isLoadingGenerate
    ? t("navBlockerQuestionsGenerating")
    : t("navBlockerUnpublishedChanges");

  useNavigationBlocker(navBlockerWhen, navBlockerMessage);

  return (
    <div>
      <Header
        breadcrumbs={[
          {
            name:
              course?.name ?? t("breadcrumbFallback.course", { ns: "header" }),
            link: `/course/${courseId}`,
          },

          {
            name: isEditing ? t("breadcrumbEdit") : t("breadcrumbGenerate"),
          },
        ]}
      >
        <div className="flex justify-between">
          <h1 className="mb-4 font-bold text-2xl">
            {isEditing ? t("pageTitleEdit") : t("pageTitleGenerate")}
          </h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-control w-full">
            <label htmlFor="courseName">
              <span className="font-semibold text-lg">{t("topic")}</span>
            </label>
            <label className="input input-sm sm:input-md input-bordered mt-sm mb-sm flex max-w-96 items-center rounded-xl ">
              <input
                type="text"
                className="grow"
                placeholder={t("placeholderSearch")}
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setTopics((prevTopics) => {
                    return prevTopics.map((topic) => {
                      return {
                        ...topic,
                        showInSearch: topic.name
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()),
                      };
                    });
                  });
                  setTopic(e.target.value);
                }}
              />
              <MagnifyingGlassIcon className="h-6 w-6 sm:h-7 sm:w-7 " />
            </label>
            <div className="my-1 max-h-96 max-w-xl overflow-y-auto">
              {topics
                .filter((topicElement) => topicElement.showInSearch)
                .map((topicElement, index) => (
                  <button
                    type="button"
                    key={topicElement.name}
                    className={`btn btn-sm m-1 ${
                      topicElement.name === topic
                        ? "btn-primary"
                        : "border-2 hover:border-primary"
                    }`}
                    onClick={() => {
                      const newTopics = [...topics];
                      newTopics[index].isActive = !newTopics[index].isActive;
                      setTopics(newTopics);

                      if (topicElement.name === topic) {
                        setTopic("");
                      } else {
                        setTopic(topicElement.name);
                      }
                    }}
                  >
                    {topicElement.name}
                  </button>
                ))}
            </div>
            {!isEditing && (
              <div className="form-control w-full">
                <label htmlFor="keywords">
                  <span className="font-semibold text-lg">{t("keywords")}</span>
                </label>
                <input
                  id="keywords"
                  required
                  minLength={3}
                  maxLength={500}
                  placeholder={t("keywordsPlaceholder")}
                  value={keywords}
                  onChange={(e) => {
                    setKeywords(e.target.value);
                  }}
                  className="input mb-2 w-full resize-none overflow-hidden text-neutral-content"
                />
              </div>
            )}
          </div>
          {!isEditing && (
            <div className="form-control flex w-full flex-row">
              <div className="flex flex-col">
                <span className="font-semibold text-lg">
                  {t("nrQuestionsMC")}
                </span>
                <select
                  className="select w-full max-w-xs"
                  value={countMultipleChoiceQuestions}
                  onChange={(e) =>
                    setCountMultipleChoiceQuestions(Number(e.target.value))
                  }
                >
                  {Array.from(
                    { length: MAX_QUESTIONS_MC + 1 },
                    (_, i) => i,
                  ).map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ml-lg flex flex-col">
                <span className="font-semibold text-lg">
                  {t("nrQuestionsFreeText")}
                </span>
                <select
                  className="select w-full max-w-xs"
                  value={countFreeTextQuestions}
                  onChange={(e) =>
                    setCountFreeTextQuestions(Number(e.target.value))
                  }
                >
                  {Array.from(
                    { length: MAX_QUESTIONS_FREE_TEXT + 1 },
                    (_, i) => i,
                  ).map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="form-control flex w-full flex-row">
            <div className="flex flex-col">
              <label htmlFor="difficulty">
                <span className="font-semibold text-lg">{t("difficulty")}</span>
              </label>
              <select
                id="difficulty"
                className="select w-full max-w-xs"
                value={difficulty}
                onChange={(e) => {
                  setDifficulty(e.target.value as QuestionDifficulty);
                  if (isEditing) {
                    setIsShouldFetchAll(e.target.value === DIFFICULTY_ALL);
                  }
                }}
              >
                {isEditing && (
                  <option value={DIFFICULTY_ALL}>
                    {t(`difficultyOptions.${DIFFICULTY_ALL}`)}
                  </option>
                )}
                {Object.values(QuestionDifficulty).map((value) => (
                  <option key={value} value={value}>
                    {t(`difficultyOptions.${value}`)}
                  </option>
                ))}
              </select>
            </div>
            {!isEditing && (
              <div className="ml-lg flex flex-col">
                <label htmlFor="language">
                  <span className="font-semibold text-lg">{t("language")}</span>
                </label>
                <select
                  id="language"
                  className="select w-full max-w-xs"
                  value={language}
                  onChange={(e) => {
                    setLanguage(e.target.value as SupportedLanguage);
                  }}
                >
                  {Object.values(SupportedLanguage).map((value) => (
                    <option key={value} value={value}>
                      {t(`languageOptions.${value}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {!isEditing && (
            <div
              id="advanced-settings"
              className="collapse-arrow collapse border border-base-300"
            >
              <input type="checkbox" className="peer" />
              <div className="collapse-title pr-2 pl-2 font-semibold text-lg md:pr-4 md:pl-4">
                {t("advancedSettings")}
              </div>
              <div className="collapse-content pr-2 pl-2 md:pr-4 md:pl-4">
                <div className="form-control w-full">
                  <div className="font-semibold text-lg">{t("aiModel")}</div>
                  <select
                    className="select w-full max-w-82"
                    value={modelType}
                    onChange={(e) => setModelType(e.target.value as ModelTypes)}
                  >
                    {availableModels.map((model) => (
                      <option key={model.modelType} value={model.modelType}>
                        {model.modelType} ({t("quality")}: {model.qualityRating}
                        /10, {t("speed")}: {model.speedRating}/10)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-control mt-sm w-full">
                  <label htmlFor="userInstructions">
                    <span className="font-semibold text-lg">
                      {t("userInstructions")}
                    </span>
                  </label>
                  <div className="mt-1 mb-1 text-xs md:text-sm">
                    {t("userInstructionsInfo")}
                  </div>
                  <textarea
                    id="userInstructions"
                    rows={1}
                    minLength={3}
                    maxLength={500}
                    placeholder={t("userInstructionsPlaceholder")}
                    value={userInstructions}
                    onChange={(e) => setUserInstructions(e.target.value)}
                    className="textarea w-full"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex">
            <button
              type="submit"
              className={"btn btn-primary w-48 shadow-sm"}
              onClick={() => {
                handleSubmit;
              }}
              disabled={
                isLoadingGenerate || (!isEditing && questions.length > 0)
              }
            >
              {isLoadingGenerate ? (
                <span className="loading loading-spinner loading-md" />
              ) : (
                <>
                  {isEditing ? t("loadQuestionsButton") : t("generateButton")}
                </>
              )}
            </button>
          </div>
        </form>
        {(questions.length > 0 || isLoadingGenerate) && (
          <>
            <div className="divider divider-primary" />
            {isEditing ? (
              <h1 className="my-md font-bold text-2xl">
                <Trans
                  t={t}
                  i18nKey="reviewQuestions"
                  values={{
                    topic: activeTopic,
                    difficulty: t(`difficultyOptions.${activeDifficulty}`),
                  }}
                  components={{
                    b: <b />,
                    u: <u />,
                  }}
                />
              </h1>
            ) : (
              <h1 className="mt-lg font-bold text-2xl">{t("reviewHeader")}</h1>
            )}

            {isLoadingGenerate && !isEditing ? (
              <QuestionsSkeletonContainer
                countMultipleChoice={countMultipleChoiceQuestions}
                countFreeText={countFreeTextQuestions}
                isEditPage={isEditing}
              />
            ) : (
              questions.map((question: QuestionDTO, index: number) => (
                <EditQuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  setQuestionToEdit={setQuestionToEdit}
                  modalIdDelete="delete_modal"
                  modalIdEdit="edit_modal"
                  isEditPage={isEditing}
                  setResetEditMask={setResetEditMask}
                  setShouldFetch={setShouldFetch}
                  setQuestionsListParent={setQuestions}
                />
              ))
            )}

            {!isLoadingGenerate && (
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  if (!isEditing) {
                    handlePublish();
                  } else {
                    navigate(`/course/${courseId}`);
                  }
                }}
              >
                {isEditing ? t("buttonDone") : t("publishButton")}
              </button>
            )}
          </>
        )}
        <Modal
          id="delete_modal"
          confirmVariant={ModalVariant.DESTRUCTIVE}
          title={t("deleteModalTitle")}
          content={t("confirmationDelete")}
          onConfirm={() => {
            if (questionToEdit) {
              handleDelete(questionToEdit.id);

              setQuestions((prevQuestions) =>
                prevQuestions.filter((q) => q.id !== questionToEdit.id),
              );
            }
          }}
          onConfirmLabel={t("delete", { ns: "commons" })}
          onCancel={() => {}}
        />
        {questionToEdit && (
          <EditQuestionModal
            question={questionToEdit}
            modalIdEdit="edit_modal"
            resetMask={resetEditMask}
            setQuestionsListParent={setQuestions}
            setShouldFetch={setShouldFetch}
          />
        )}
      </Header>
    </div>
  );
}

export default ManageQuizPage;
